// The flight manual's operating speeds and cruise table for an aircraft
// (flight.aircraft_speeds, flight.aircraft_cruise_performance), and how the
// aircraft page lays them out. Speeds are KIAS, weights lb, altitudes ft,
// fuel flow US gallons per hour: the manual's own units.
//
// Pure; see navigation.ts.

export const SPEED_CATEGORIES = ['normal', 'emergency'] as const
export type SpeedCategory = (typeof SPEED_CATEGORIES)[number]

// The order a pilot reads them in. A code the club entered that is not here
// is shown after these, so a new speed needs no deploy to appear.
export const SPEED_CODES: Record<SpeedCategory, readonly string[]> = {
  normal: [
    'takeoff_climb_out',
    'short_field_takeoff',
    'short_field_takeoff_climb',
    'climb',
    'best_rate_of_climb',
    'best_angle_of_climb',
    'approach',
    'short_field_approach',
    'balked_landing',
    'turbulence_penetration',
  ],
  emergency: [
    'engine_failure_after_takeoff',
    'engine_failure_in_flight',
    'max_glide',
    'emergency_landing',
    'precautionary_landing',
    'ditching',
    'icing_approach',
    'maneuvering',
  ],
}

const FLAPS_ORDER = ['up', '10', '20', '30', '40', 'down']

// A row of GET /api/aircraft/:registration/flight-manual.
export interface AircraftSpeed {
  category: SpeedCategory
  speedCode: string
  flaps: string | null
  weightLb: number | null
  altitudeFt: number | null
  kiasMin: number
  kiasMax: number
  notes: string | null
}

export interface AircraftCruiseRow {
  pressureAltitudeFt: number
  rpm: number
  percentBhp: number
  fuelUsgPerHour: number
}

/** "75–85" for a range, "70" for a single figure. */
export function formatKias(min: number, max: number): string {
  return min === max ? String(min) : `${min}–${max}`
}

export interface SpeedGroup {
  speedCode: string
  rows: AircraftSpeed[]
}

/**
 * One category's speeds grouped by code, in reading order; within a code the
 * heaviest weight first, then the lowest altitude, then flaps up to down.
 */
export function groupSpeeds(speeds: readonly AircraftSpeed[], category: SpeedCategory): SpeedGroup[] {
  const order = SPEED_CODES[category]
  const rank = (code: string) => {
    const i = order.indexOf(code)
    return i === -1 ? order.length : i
  }
  const flapsRank = (flaps: string | null) => (flaps === null ? -1 : FLAPS_ORDER.indexOf(flaps))
  const byCode = new Map<string, AircraftSpeed[]>()
  for (const speed of speeds) {
    if (speed.category !== category) continue
    byCode.set(speed.speedCode, [...(byCode.get(speed.speedCode) ?? []), speed])
  }
  return [...byCode.entries()]
    .map(([speedCode, rows]) => ({
      speedCode,
      rows: rows.sort(
        (a, b) =>
          (b.weightLb ?? 0) - (a.weightLb ?? 0) ||
          (a.altitudeFt ?? 0) - (b.altitudeFt ?? 0) ||
          flapsRank(a.flaps) - flapsRank(b.flaps),
      ),
    }))
    .sort((a, b) => rank(a.speedCode) - rank(b.speedCode) || a.speedCode.localeCompare(b.speedCode))
}

export interface CruiseAltitude {
  pressureAltitudeFt: number
  rows: AircraftCruiseRow[]
}

/** The cruise table by pressure altitude, lowest first, highest rpm first within one. */
export function groupCruise(rows: readonly AircraftCruiseRow[]): CruiseAltitude[] {
  const byAltitude = new Map<number, AircraftCruiseRow[]>()
  for (const row of rows) {
    byAltitude.set(row.pressureAltitudeFt, [...(byAltitude.get(row.pressureAltitudeFt) ?? []), row])
  }
  return [...byAltitude.entries()]
    .sort(([a], [b]) => a - b)
    .map(([pressureAltitudeFt, group]) => ({ pressureAltitudeFt, rows: group.sort((a, b) => b.rpm - a.rpm) }))
}
